import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import { Icon } from "@iconify/react";
import CallEndIcon from "@mui/icons-material/CallEnd";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import PhonePausedIcon from "@mui/icons-material/PhonePaused";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SaveIcon from "@mui/icons-material/Save";
import StopIcon from "@mui/icons-material/Stop";

import {
  CircularProgress,
  Collapse,
  Fade,
  IconButton,
  Tooltip,
} from "@mui/material";

import "./CallControl.css";
import RenderTimeout from "./RenderTimeout";
import Loader from "../Components/Loader";
import ResultCallModal from "../Components/ResultCallModal";
import Timer from "../Helper/Timer";
import { ReactComponent as LegendACW } from "../Images/LegendACW.svg";
import { ReactComponent as LegendAUX } from "../Images/LegendAUX.svg";
import { stateAction } from "../Store/Actions/AuthAction";

import {
  getActiveCall,
  getCampaignISPlan,
  setOriginate,
  setParkCall,
  setTransfer,
} from "../Store/Actions/CallAction";

import { getActiveAgent } from "../Store/Actions/MonitoringAction";
import { saveAssignmentWithSchedule } from "../Store/Actions/OutboundAction";

import {
  clearSessionCall,
  endConsult,
  endSession,
  holdCall,
  muteCall,
} from "../Store/SIP/call";

import {
  SET_AUTO_DIAL,
  SET_AUTO_DIAL_CAMPAIGN,
  SET_AUTO_DIAL_PAUSED,
  SET_DIAL_MODE,
  SET_PENDING_STATUS_REASON,
} from "../Store/Types/AgentTypes";

import {
  RESET_CALL_STATE,
  SET_IS_CONSULTING,
  SET_IS_CONTINUE_TRANSFER,
  SET_IS_PARKING,
  SHOW_CONTACT_MODAL,
  UPDATE_CALL_STATE,
  UPDATE_STATECALL,
} from "../Store/Types/CallTypes";

import {
  SHOW_ADD_CONTACT,
  SHOW_CREATE_TICKET,
} from "../Store/Types/ContactTypes";


export default function CallControl({ changePage }) {
  const [activeAgents, setActiveAgents] = useState([]);
  const [ISPlan, setISPlan] = useState([]);
  const [isAgentListLoading, setIsAgentListLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isResultCallModalShown, setIsResultCallModalShown] = useState(false);
  const [isTransferShown, setIsTransferShown] = useState(false);
  const [transferTo, setTransferTo] = useState({});
  const dispatch = useDispatch();

  const { isAutoDialPaused, isInAutoDial } = useSelector(
    (state) => state.agent
  );

  const { agentPermission, inboundSaveTicket, myInfo } = useSelector(
    (state) => state.auth
  );

  const {
    campaignID,
    contactID,
    goToACW,
    isConsulting,
    isInACW,
    isInCall,
    isMuted,
    onHold,
    phoneTypeID,
    phoneNumber,
    stateCall,
    stateCode,
    type,
    ACWMode,
    ACWDuration,
    callTime,
    isTelemarketing,
  } = useSelector((state) => state.call);

  const { noContactFound } = useSelector((state) => state.contact);
  const { currentAssignmentID } = useSelector((state) => state.outbound);

  useEffect(() => {
    resetStates();
  }, []);

  useEffect(() => {
    const fetchData = () => {
      getActiveAgent(myInfo.user.AgentID)
        .then((response) => {
          if (response.status) {
            process.env.NODE_ENV === "development" &&
              console.log("Active agents: ", response.data);

            setActiveAgents(response.data);
          } else {
            console.log("error", response.error);
          }

          setIsAgentListLoading(false);
        })
        .catch((err) => {
          console.log("err", err);
          setIsAgentListLoading(false);
        });
    };

    // Fetch data on first render
    fetchData();

    // Fetch data every five seconds
    const interval = setInterval(() => {
      isInCall && fetchData();
    }, 5000);

    if (!isTransferShown) {
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [isTransferShown]);

  const renderDiscardButton = () => {
    const discardPermission = agentPermission.find(
      (permission) => permission.PermissionCode === "Status.DiscardACW"
    );

    if (
      process.env.NODE_ENV === "development" || // Enable during development
      (discardPermission && discardPermission.PermissionAccess === "A")
    ) {
      return (
        <Tooltip title="Discard" arrow>
          <IconButton
            sx={{
              width: 39,
              height: 39,
              bgcolor: "#727272",
              color: "#FFF",
              boxShadow: "0px 2px 18px 1px rgba(0, 0, 0, 0.18)",
              "&:hover": { backgroundColor: "#848484" },
            }}
            onClick={() => {
              isTelemarketing &&
                saveAssignmentWithSchedule({
                  actioncode: 500,
                  actioncodereason: "discard",
                  AgentTSSID: myInfo.user.AgentTSS.AgentID,
                  callsource: "AUTOMATICCALL",
                  calltime: callTime.toISOString(),
                  campaignid: campaignID,
                  contactid: contactID,
                  discard: true,
                  duration: moment().diff(callTime, "seconds"),
                  nextaction: "discard",
                  phoneno: phoneNumber,
                  phonetype: phoneTypeID,
                  recordingfile: stateCall.RecFileName,
                  remarks: "discard",
                  source: "TSS",
                  trycount: 1,
                  uniqueid: currentAssignmentID,
                })
                  .then((response) => {
                    if (response.status) {
                      console.log(
                        "Output interaction ID",
                        response.data.interactionid
                      );

                      toast.success("Interaction discarded", {
                        autoClose: 3000,
                      });
                    } else {
                      console.log("error", response.error);
                      toast.error("Error discarding interaction.", {
                        autoClose: 3000,
                      });
                    }
                  })
                  .catch((err) => {
                    console.log("err", err);
                    toast.error("Error discarding interaction.", {
                      autoClose: 3000,
                    });
                  });

              dispatch({ type: RESET_CALL_STATE });
              dispatch({ type: SET_AUTO_DIAL, payload: false });

              dispatch({
                type: SET_DIAL_MODE,
                payload: "",
              });

              dispatch({
                type: SET_AUTO_DIAL_CAMPAIGN,
                payload: {},
              });

              dispatch(stateAction("READY", undefined, "ACW", "Green"));
            }}
          >
            <Icon height="30" icon="tabler:trash" width="30" />
          </IconButton>
        </Tooltip>
      );
    } else {
      return null;
    }
  };

  const renderPauseAutoDialButton = () => {
    if (isInAutoDial) {
      return (
        <Tooltip title="Pause autodial" arrow>
          <IconButton
            disabled={isAutoDialPaused}
            sx={{
              width: 39,
              height: 39,
              bgcolor: "#727272",
              color: "#FFF",
              boxShadow: "0px 2px 18px 1px rgba(0, 0, 0, 0.18)",
              "&:hover": { backgroundColor: "#848484" },
            }}
            onClick={() => {
              toast.info("Autodial will be paused after this assignment", {
                autoClose: 3000,
              });

              dispatch({ type: SET_PENDING_STATUS_REASON, payload: "REST" });

              dispatch({
                type: SET_AUTO_DIAL_PAUSED,
                payload: true,
              });
            }}
          >
            <Icon height="30" icon="mdi:pause" width="30" />
          </IconButton>
        </Tooltip>
      );
    } else {
      return null;
    }
  };

  const renderTransferButton = () => {
    const transferPermission = agentPermission.find(
      (permission) => permission.PermissionCode === "Call.Transfer"
    );

    if (transferPermission && transferPermission.PermissionAccess === "A") {
      return (
        <Tooltip title="Transfer" arrow>
          <IconButton
            sx={{
              width: 29,
              height: 29,
              padding: 0,
              bgcolor: "#727272",
              color: "#FFF",
              boxShadow: "0px 2px 18px 1px rgba(0, 0, 0, 0.18)",
              "&:hover": { backgroundColor: "#848484" },
            }}
            onClick={() => setIsTransferShown(!isTransferShown)}
          >
            <Icon height="22" icon="wpf:call-transfer" width="22" />
          </IconButton>
        </Tooltip>
      );
    } else {
      return null;
    }
  };

  const renderResultCallButton = () => {
    if (type === "inbound" || type === "INBOUND") {
      return "";
    } else {
      return (
        <Tooltip
          title={
            (contactID && inboundSaveTicket !== "True") ||
            type === "outbound" ||
            type === "OUTBOUND"
              ? "Show call result form"
              : noContactFound
              ? "Create new contact"
              : "Create new ticket"
          }
          arrow
        >
          <IconButton
            sx={{
              width: 39,
              height: 39,
              bgcolor: "#0343C7",
              color: "#FFF",
              boxShadow: "0px 2px 18px 1px rgba(0, 0, 0, 0.18)",
              "&:hover": { backgroundColor: "#063493" },
            }}
            disabled={isLoading}
            onClick={() => {
              if (type === "outbound" || type === "OUTBOUND") {
                setIsLoading(true);

                getCampaignISPlan(campaignID)
                  .then((response) => {
                    if (response.status) {
                      setISPlan(response.data);
                    } else {
                      console.log("error", response.error);
                    }

                    setIsLoading(false);
                    setIsResultCallModalShown(true);
                  })
                  .catch((err) => {
                    console.log("err", err);
                    setIsLoading(false);
                  });
              } else {
                if (contactID && inboundSaveTicket !== "True") {
                  changePage("contact");
                  dispatch({ type: SHOW_CONTACT_MODAL });
                } else {
                  changePage("contact");

                  if (noContactFound) {
                    dispatch({
                      type: SHOW_ADD_CONTACT,
                      payload: true,
                    });
                  } else {
                    dispatch({
                      type: SHOW_CREATE_TICKET,
                      payload: true,
                    });
                  }
                }
              }
            }}
          >
            {isLoading ? (
              <CircularProgress
                color="inherit"
                className="callControl_progress"
              />
            ) : (
              <SaveIcon />
            )}
          </IconButton>
        </Tooltip>
      );
    }
  };

  // const ResultCallButton = () => (
  //   <Tooltip title="Show Result Call Form" arrow>
  //     <IconButton
  //       sx={{
  //         width: 39,
  //         height: 39,
  //         bgcolor: "#0343C7",
  //         color: "#FFF",
  //         boxShadow: "0px 2px 18px 1px rgba(0, 0, 0, 0.18)",
  //         "&:hover": { backgroundColor: "#063493" },
  //       }}
  //       disabled={isLoading}
  //       onClick={() => {
  //         if (type === "outbound") {
  //           setIsLoading(true);

  //           getCampaignISPlan(campaignID)
  //             .then((response) => {
  //               if (response.status) {
  //                 setISPlan(response.data);
  //               } else {
  //                 console.log("error", response.error);
  //               }

  //               setIsLoading(false);
  //               setIsResultCallModalShown(true);
  //             })
  //             .catch((err) => {
  //               console.log("err", err);
  //               setIsLoading(false);
  //             });
  //         } else {
  //           if (contactID) {
  //             changePage("contact");
  //             dispatch({ type: SHOW_CONTACT_MODAL });
  //           } else {
  //             changePage("contact");
  //             dispatch({ type: SHOW_CONTACT_MODAL });
  //           }
  //         }
  //       }}
  //     >
  //       {isLoading ? (
  //         <CircularProgress color="inherit" className="callControl_progress" />
  //       ) : (
  //         <SaveIcon />
  //       )}
  //     </IconButton>
  //   </Tooltip>
  // );

  // TODO: refactor to use agentList component
  const agentList = () => {
    let icon, color, status;

    const renderOption = (agent, index) => {
      switch (agent.CurrentState) {
        case "BUSY":
          icon = "tabler:alert-square-rounded-filled";
          color = "#FF0000";
          status = "Busy";
          break;

        case "READY":
          icon = "tabler:square-rounded-check";
          color = "#00FF00";
          status = "Ready";
          break;

        case "ACW":
          color = "#f0e76f";
          status = "ACW";
          break;

        case "AUX":
          color = "#EE7314";
          status = "AUX";
          break;

        case "NOTBOUND":
          icon = "tabler:square-rounded-x";
          color = "#727272";
          status = "Not Bound";
          break;

        default:
          break;
      }

      return (
        <div
          className={`agentList_option ${
            transferTo.AgentID === agent.AgentID && "selected"
          }`}
          key={index}
          onClick={() => setTransferTo(agent)}
        >
          {agent.CurrentState === "ACW" ? (
            <LegendACW style={{ color: color }} />
          ) : agent.CurrentState === "AUX" ? (
            <LegendAUX style={{ color: color }} />
          ) : (
            <Icon color={color} height="26" icon={icon} width="26" />
          )}
          <p className="option_agentName">{`${agent.FirstName} ${agent.LastName}`}</p>
          <p className="option_agentStatus">{status}</p>
        </div>
      );
    };

    return (
      <div className="transfer_agentList">
        {isAgentListLoading ? (
          <Loader />
        ) : (
          activeAgents
            .filter((agent) => agent.AgentID !== myInfo.user.AgentID)
            .map((agent, index) => renderOption(agent, index))
        )}
      </div>
    );
  };

  const resetStates = () => {
    setIsTransferShown(false);
    setTransferTo({});

    dispatch({
      type: SET_IS_CONSULTING,
      payload: false,
    });
  };

  const handleEndCall = () => {
    dispatch({
      type: UPDATE_CALL_STATE,
      payload: { stateCode: "16" },
    });

    console.log(
      "Call ended",
      "stateCode: ",
      stateCode,
      "stateCall: ",
      stateCall,
      "isInCall: ",
      isInCall
    );

    stateCall.StateCode ? dispatch(endSession()) : dispatch(clearSessionCall());
    resetStates();
  };

  return (
    <Fade appear in={isInCall} timeout={500} unmountOnExit>
      <div className="callControl">
        <div className="callControl_content">
          <div className="callControl_callInfo">
            <div className="callControl_number">
              <span
                className={`
                callControl_statusIndicator in-app
                ${stateCode !== "6" && !isInACW ? "process" : ""}
                ${isInACW ? "disconnected" : ""}
              `}
              />
              {/* {phoneNumber?.charAt(0) === "0" ? phoneNumber : "+" + phoneNumber} */}
              {phoneNumber?.replace(/\+62/g, "0")}
              {/* {stateCall.IsWACall && " - from WhatsApp"} */}

              {stateCall?.ACDDeviceName?.toLowerCase().includes("whatsapp") &&
                " - from WhatsApp"}
            </div>

            <div className="callControl_callDetails">
              <div className="callControl_callStatus">
                <span
                  className={`
                  callControl_statusIndicator
                  ${stateCode !== "6" && !isInACW ? "process" : ""}
                  ${isInACW ? "disconnected" : ""}
                `}
                />

                {(stateCode === "" || stateCode === "6") && isInACW ? (
                  <span className="callControl_statusText disconnected">
                    Disconnected
                  </span>
                ) : (
                  <span
                    className={`callControl_statusText ${
                      stateCode !== "6" ? "process" : ""
                    }`}
                  >
                    {stateCode === "16"
                      ? "Disconnecting..."
                      : stateCode !== "6"
                        ? "Ringing"
                        : "Connected"}
                  </span>
                )}
              </div>

              <span className="callControl_timer">
                {(stateCode === "" || stateCode === "6") && isInACW ? (
                  <StopIcon />
                ) : (
                  <PlayArrowIcon />
                )}

                <Timer isPaused={stateCode !== "6" || isInACW ? true : false} />
              </span>
            </div>
          </div>

          <div className="callControl_buttonGroup">
            {isInACW && (
              <>
                {renderDiscardButton()}
                {renderPauseAutoDialButton()}
                {renderResultCallButton()}
              </>
            )}

            {stateCode === "6" && !isInACW && (
              <>
                <div className="transfer-button">{renderTransferButton()}</div>

                <Tooltip title={onHold ? "Resume" : "Hold"} arrow>
                  <IconButton
                    disabled={isConsulting}
                    sx={{
                      width: 29,
                      height: 29,
                      bgcolor: onHold ? "#FFF" : "#727272",
                      color: onHold ? "#727272" : "#FFF",
                      boxShadow: "0px 2px 18px 1px rgba(0, 0, 0, 0.18)",
                      "&:hover": {
                        backgroundColor: onHold ? "#c4c4c4" : "#848484",
                      },
                    }}
                    onClick={() => {
                      if (stateCall.IsParked) {
                        setOriginate({
                          channel: myInfo.user.Ext,
                          callerid: myInfo.user.Ext.match(/\d+/)[0],
                          exten: stateCall.ParkingSpace,
                        })
                          .then((response) => {
                            if (response.status) {
                              console.log("setOriginate", response);

                              if (
                                response.data.response.includes(
                                  "Response:Success"
                                )
                              ) {
                                console.log("Call retrieved");
                                dispatch(holdCall());
                                resetStates();
                              } else {
                                toast.error("Error retrieving call", {
                                  autoClose: 3000,
                                });
                              }
                            } else {
                              console.log("setOriginate", response);

                              toast.error("Error retrieving call", {
                                autoClose: 3000,
                              });
                            }
                          })
                          .catch((err) => {
                            console.log("err", err);

                            toast.error("Error retrieving call", {
                              autoClose: 3000,
                            });
                          });
                      } else {
                        dispatch(holdCall());
                      }
                    }}
                  >
                    <PhonePausedIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title={isMuted ? "Unmute" : "Mute"} arrow>
                  <IconButton
                    sx={{
                      width: 29,
                      height: 29,
                      bgcolor: "#727272",
                      color: "#FFF",
                      boxShadow: "0px 2px 18px 1px rgba(0, 0, 0, 0.18)",
                      "&:hover": { backgroundColor: "#848484" },
                    }}
                    disabled={onHold}
                    onClick={() => dispatch(muteCall())}
                  >
                    {isMuted ? <MicIcon /> : <MicOffIcon />}
                  </IconButton>
                </Tooltip>

                {renderResultCallButton()}
                {/* {!campaignID ? "" : renderResultCallButton()} */}
              </>
            )}

            {!isInACW && (
              <Tooltip title="End call" arrow>
                <IconButton
                  disabled={onHold}
                  sx={{
                    width: 39,
                    height: 39,
                    bgcolor: "#F24E1E",
                    color: "#FFF",
                    boxShadow: "0px 2px 18px 1px rgba(0, 0, 0, 0.18)",
                    "&:hover": { backgroundColor: "#ff7d58" },
                  }}
                  onClick={handleEndCall}
                >
                  <CallEndIcon />
                </IconButton>
              </Tooltip>
            )}
          </div>

          <Collapse in={isTransferShown} unmountOnExit>
            <div className="callControl_transfer">
              {agentList()}

              <div className="transfer_consult_return">
                {Object.keys(transferTo).length !== 0 && (
                  <div className="transfer_consult">
                    <div className="consult_callInfo">
                      <span className="callInfo_agentName">
                        {`${transferTo.FirstName} ${transferTo.LastName}`}
                      </span>

                      {isConsulting ? (
                        <div className="callControl_callStatus">
                          <span className="callControl_statusIndicator" />
                          <span className="callControl_statusText">
                            Connected
                          </span>
                        </div>
                      ) : (
                        <span className="callInfo_agentStatus">
                          {transferTo.CurrentState.toLowerCase()}
                        </span>
                      )}
                    </div>

                    {isConsulting && (
                      <span className="callControl_timer">
                        <PlayArrowIcon />
                        <Timer />
                      </span>
                    )}

                    <div className="callInfo_buttons">
                      {isConsulting ? (
                        <button
                          className="buttons_consult end"
                          disabled={transferTo.CurrentState !== "READY"}
                          onClick={() => {
                            dispatch({
                              type: SET_IS_CONSULTING,
                              payload: false,
                            });

                            dispatch(endConsult());
                          }}
                        >
                          <Icon
                            height="22"
                            icon="material-symbols:call-end"
                            width="22"
                          />
                        </button>
                      ) : (
                        <button
                          className="buttons_consult"
                          disabled={transferTo.CurrentState !== "READY"}
                          onClick={() => {
                            console.log("start consult");

                            dispatch({
                              type: SET_IS_CONSULTING,
                              payload: true,
                            });

                            /* dispatch({
                              type: SET_IS_PARKING,
                              payload: true,
                            });

                            setParkCall(stateCall.ActionID, myInfo.user.Ext)
                              .then((response) => {
                                if (response.status) {
                                  console.log("setParkCall", response);

                                  if (
                                    response.data.response.includes(
                                      "Park successful"
                                    )
                                  ) {
                                    dispatch({
                                      type: SET_IS_PARKING,
                                      payload: false,
                                    });

                                    dispatch(holdCall());

                                    setOriginate({
                                      channel: myInfo.user.Ext,
                                      callerid: myInfo.user.Ext.match(/\d+/)[0],
                                      exten: transferTo.Ext,
                                    })
                                      .then((response) => {
                                        if (response.status) {
                                          console.log("setOriginate", response);

                                          if (
                                            response.data.response.includes(
                                              "Response:Success"
                                            )
                                          ) {
                                            console.log("connected to target");

                                            getActiveCall({
                                              StateCode: "6",
                                              AgentID: transferTo.AgentID,
                                              PhoneNo: myInfo.user.AgentName,
                                              IsConsult: true,
                                            });
                                          } else {
                                            toast.error(
                                              "Error connecting to agent",
                                              {
                                                autoClose: 3000,
                                              }
                                            );
                                          }
                                        } else {
                                          console.log("setOriginate", response);

                                          toast.error(
                                            "Error connecting to agent",
                                            {
                                              autoClose: 3000,
                                            }
                                          );
                                        }
                                      })
                                      .catch((err) => {
                                        console.log("err", err);

                                        toast.error(
                                          "Error connecting to agent",
                                          {
                                            autoClose: 3000,
                                          }
                                        );
                                      });
                                  } else {
                                    toast.error("Error holding call", {
                                      autoClose: 3000,
                                    });
                                  }
                                } else {
                                  console.log("setTransfer", response);

                                  toast.error("Error holding call", {
                                    autoClose: 3000,
                                  });
                                }
                              })
                              .catch((err) => {
                                console.log("err", err);

                                toast.error("Error holding call", {
                                  autoClose: 3000,
                                });
                              }); */

                            !onHold && dispatch(holdCall());

                            setOriginate({
                              channel: myInfo.user.Ext,
                              callerid: myInfo.user.Ext.match(/\d+/)[0],
                              exten: transferTo.Ext,
                            })
                              .then((response) => {
                                if (response.status) {
                                  console.log("setOriginate", response);

                                  if (
                                    response.data.response.includes(
                                      "Response:Success"
                                    )
                                  ) {
                                    console.log("connected to target");

                                    getActiveCall({
                                      StateCode: "6",
                                      AgentID: transferTo.AgentID,
                                      PhoneNo: myInfo.user.AgentName,
                                      IsConsult: true,
                                    });
                                  } else {
                                    toast.error("Error connecting to agent", {
                                      autoClose: 3000,
                                    });
                                  }
                                } else {
                                  console.log("setOriginate", response);

                                  toast.error(
                                    "Error connecting to agent: " +
                                      response?.error,
                                    { autoClose: 3000 }
                                  );
                                }
                              })
                              .catch((err) => {
                                console.log("err", err);

                                toast.error("Error connecting to agent: " + err, {
                                  autoClose: 3000,
                                });
                              });
                          }}
                        >
                          <Icon
                            height="22"
                            icon="material-symbols:call"
                            width="22"
                          />
                        </button>
                      )}

                      <button
                        className="buttons_transfer"
                        disabled={
                          transferTo.CurrentState !== "READY" || isConsulting
                        }
                        onClick={() => {
                          console.log("start transfer");

                          let payloadActiveCall = {
                            StateCode: "6",
                            AgentID: transferTo.AgentID,
                            PhoneNo: phoneNumber,
                            CallType: stateCall.CallType,
                            ContactID: contactID,
                            GoToACW: goToACW,
                            IsTransfer: true,
                            UniqueID: stateCall.UniqueID,
                            CampaignID: campaignID,
                            PhoneTypeID: phoneTypeID,
                            ActionID: stateCall.ActionID,
                            ACDDeviceID: stateCall.ACDDeviceID,
                            Channel: stateCall.Channel,
                            RecFileName: stateCall.RecFileName,

                            TransferCount: stateCall.TransferCount
                              ? stateCall.TransferCount + 1
                              : 1,
                          };

                          let toastSuccess = () =>
                            toast.success("Call transferred successfully", {
                              autoClose: 3000,
                            });

                          let toastError = (error) =>
                            toast.error("Error transferring call: " + error, {
                              autoClose: 3000,
                            });

                          // if (stateCall.IsParked) {
                          // if (onHold) {
                          //   setOriginate({
                          //     channel: transferTo.AddressName,
                          //     callerid: transferTo.Ext,
                          //     // exten: stateCall.ParkingSpace,
                          //     exten: stateCall.Channel,
                          //   })
                          //     .then((response) => {
                          //       if (response.status) {
                          //         console.log("setOriginate", response);

                          //         if (
                          //           response.data.response.includes(
                          //             "Response:Success"
                          //           )
                          //         ) {
                          //           getActiveCall(payloadActiveCall);
                          //           toastSuccess();
                          //           handleEndCall();
                          //         } else {
                          //           toastError();
                          //         }
                          //       } else {
                          //         console.log("setOriginate", response);
                          //         toastError(response?.error);
                          //         handleEndCall();
                          //       }
                          //     })
                          //     .catch((err) => {
                          //       console.log("err", err);
                          //       toastError(err);
                          //     });
                          // } else {
                            onHold && dispatch(holdCall());

                            if (stateCall.CallType === "INBOUND") {
                              setTransfer(
                                transferTo.Ext,
                                stateCall.Channel,
                                "INBOUND"
                              )
                                .then((response) => {
                                  if (response.status) {
                                    console.log("setTransfer", response);

                                    if (
                                      response.data.response.includes(
                                        "Response:Success"
                                      )
                                    ) {
                                      dispatch({
                                        type: SET_IS_CONTINUE_TRANSFER,
                                        payload: true,
                                      });

                                      getActiveCall(payloadActiveCall);
                                      toastSuccess();
                                    } else {
                                      toastError();
                                    }

                                    handleEndCall();
                                  } else {
                                    console.log("setTransfer", response);
                                    toastError(response?.error);
                                  }
                                })
                                .catch((err) => {
                                  console.log("err", err);
                                  handleEndCall();
                                  toastError(err);
                                });
                            } else {
                              setTransfer(
                                transferTo.Ext,
                                stateCall.Channel
                                  ? stateCall.Channel
                                  : stateCall.ActionID,
                                stateCall.Channel ? "CHANNELID" : "ACTIONID"
                              )
                                .then((response) => {
                                  if (response.status) {
                                    console.log("setTransfer", response);

                                    if (
                                      response.data.response.includes(
                                        "Response:Success"
                                      )
                                    ) {
                                      getActiveCall(payloadActiveCall);
                                      toastSuccess();
                                    } else {
                                      toastError();
                                    }

                                    handleEndCall();
                                  } else {
                                    console.log("setTransfer", response);
                                    toastError(response?.error);
                                  }
                                })
                                .catch((err) => {
                                  console.log("err", err);
                                  handleEndCall();
                                  toastError(err);
                                });
                            }
                          // }
                        }}
                      >
                        Transfer
                      </button>
                    </div>
                  </div>
                )}

                <button
                  className="return"
                  // disabled={isConsulting || !stateCall.IsParked}
                  disabled={isConsulting}
                  onClick={() => {
                    // setOriginate({
                    //   channel: myInfo.user.Ext,
                    //   callerid: myInfo.user.Ext.match(/\d+/)[0],
                    //   exten: stateCall.ParkingSpace,
                    // })
                    //   .then((response) => {
                    //     if (response.status) {
                    //       console.log("setOriginate", response);

                    //       if (
                    //         response.data.response.includes("Response:Success")
                    //       ) {
                    //         console.log("Call retrieved");

                    //         dispatch({
                    //           type: UPDATE_STATECALL,
                    //           payload: {
                    //             ...stateCall,
                    //             IsParked: false,
                    //           },
                    //         });

                    //         resetStates();
                    //         dispatch(holdCall());
                    //       } else {
                    //         toast.error("Error retrieving call", {
                    //           autoClose: 3000,
                    //         });
                    //       }
                    //     } else {
                    //       console.log("setOriginate", response);

                    //       toast.error("Error retrieving call", {
                    //         autoClose: 3000,
                    //       });
                    //     }
                    //   })
                    //   .catch((err) => {
                    //     console.log("err", err);

                    //     toast.error("Error retrieving call", {
                    //       autoClose: 3000,
                    //     });
                    //   });

                    console.log("Call retrieved");
                    resetStates();
                    dispatch(holdCall());
                  }}
                >
                  <Icon height="20" icon="mdi:phone-return" width="20" />
                  Return to call
                </button>
              </div>
            </div>
          </Collapse>
        </div>

        <div className="callControl_rightSpace" />

        <ResultCallModal
          isOpen={isResultCallModalShown}
          ISPlan={ISPlan}
          setIsResultCallModalShown={setIsResultCallModalShown}
        />

        {isInACW &&
        ACWMode === "FIXTIME" &&
        isResultCallModalShown === false ? (
          <RenderTimeout ACWDuration={ACWDuration} />
        ) : (
          ""
        )}
      </div>
    </Fade>
  );
}
